import { memo, useMemo } from "react";
import { FormatCurrency } from "../../utils/FormatCurrency";
import {
  CustomTicketV2,
  EventType,
  PromoCode,
  PromoCodeType,
  TicketType,
} from "@markit/common.types";
import { ThemeStyle } from "../../hooks/useTheme";
import { getRequestTicketLabel } from "@markit/common.utils";
import { applyPromoCode } from "../../utils/eventUtils/eventUtils";

type FullEventPriceDisplayProps = {
  eventType: EventType;
  customTicket: CustomTicketV2;
  selectedCustomTicket: CustomTicketV2;
  ticketQuantity: number;
  promoCode: PromoCode | undefined;
  isTixId: boolean;
  isSoldOut: boolean;
  theme: ThemeStyle;
  namedPrice?: number; // If the named price is provided, it will be displayed instead of the custom ticket price
};

// Displays the price for a ticket option, accounting for promo, sold out, tix id
const FullEventPriceDisplay = memo(function FullEventPriceDisplayFn(
  props: FullEventPriceDisplayProps
) {
  const {
    eventType,
    customTicket,
    selectedCustomTicket,
    ticketQuantity,
    promoCode,
    isTixId,
    isSoldOut,
    theme,
    namedPrice,
  } = props;

  const priceToDisplay = useMemo(
    () => namedPrice || customTicket.price,
    [namedPrice, customTicket.price]
  );

  if (
    promoCode &&
    promoCode.type !== PromoCodeType.DOLLARS_OFF &&
    priceToDisplay > 0 &&
    selectedCustomTicket.id === customTicket.id
  ) {
    const baseForThisTypeCents = priceToDisplay * ticketQuantity;
    const discountedPerUnitCents =
      ticketQuantity > 0
        ? Math.round(
            (applyPromoCode(baseForThisTypeCents, promoCode) ??
              baseForThisTypeCents) / ticketQuantity
          )
        : priceToDisplay;
    return (
      <span className="bodyMedium" style={theme.PrimaryText}>
        {FormatCurrency(discountedPerUnitCents / 100)}
        <span className="TicketStrike">
          {FormatCurrency(priceToDisplay / 100)}
        </span>
      </span>
    );
  }
  return customTicket.type === TicketType.FREE ? (
    <span className="bodyMedium" style={theme.PrimaryText}>
      Free
    </span>
  ) : customTicket.type === TicketType.REQUEST ? (
    <span className="bodyMedium" style={theme.PrimaryText}>
      {getRequestTicketLabel(eventType)}
    </span>
  ) : (
    <div className="AlignedRow">
      {isTixId ? (
        <span className="bodyMedium" style={theme.PrimaryText}>
          $0
        </span>
      ) : null}
      <span
        className={isTixId ? "TicketStrike bodyMedium" : "bodyMedium"}
        style={{
          color: isSoldOut || isTixId ? "gray" : theme.PrimaryText.color,
        }}
      >
        {namedPrice !== undefined ? (
          namedPrice === 0 ? (
            "Free"
          ) : (
            FormatCurrency(namedPrice / 100)
          )
        ) : (
          <>
            {FormatCurrency(customTicket.price / 100)}
            {customTicket.flexiblePricing ? "±" : ""}
          </>
        )}
      </span>
    </div>
  );
});

export default FullEventPriceDisplay;
